/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { useQuery } from 'react-query'

import {
  listElasticsearchProjects,
  listObservabilityProjects,
  listSecurityProjects,
  getElasticsearchProjectLinkCandidates,
  getObservabilityProjectLinkCandidates,
  getSecurityProjectLinkCandidates,
  listWorkplaceAIProjects,
} from '@modules/project-admin-api/v1/callers'
import type { QueryFunctionReturnType, QueryHookOptions } from '@modules/query/types'
import type { ProjectID } from '@modules/ui-types/projects'

import { makeQueryKey } from './helpers'

export const useListElasticsearchProjectsQuery = (
  args: Exclude<Parameters<typeof listElasticsearchProjects>[0], undefined>['queryParameters'],
  options?: QueryHookOptions,
) =>
  useQuery<QueryFunctionReturnType<typeof listElasticsearchProjects>>(
    makeQueryKey('adminListElasticsearchProjects', args),
    () =>
      listElasticsearchProjects({
        queryParameters: args,
      }),
    options,
  )

export const useListObservabilityProjectsQuery = (
  args: Exclude<Parameters<typeof listElasticsearchProjects>[0], undefined>['queryParameters'],
  options?: QueryHookOptions,
) =>
  useQuery<QueryFunctionReturnType<typeof listObservabilityProjects>>(
    makeQueryKey('adminListObservabilityProjects', args),
    () =>
      listObservabilityProjects({
        queryParameters: args,
      }),
    options,
  )

export const useListSecurityProjectsQuery = (
  args: Exclude<Parameters<typeof listElasticsearchProjects>[0], undefined>['queryParameters'],
  options?: QueryHookOptions,
) =>
  useQuery<QueryFunctionReturnType<typeof listSecurityProjects>>(
    makeQueryKey('adminListSecurityProjects', args),
    () =>
      listSecurityProjects({
        queryParameters: args,
      }),
    options,
  )

export const useListWorkplaceAIProjectsQuery = (
  args: Exclude<Parameters<typeof listWorkplaceAIProjects>[0], undefined>['queryParameters'],
  options?: QueryHookOptions,
) =>
  useQuery<QueryFunctionReturnType<typeof listWorkplaceAIProjects>>(
    makeQueryKey('adminListWorkplaceAIProjects', args),
    () =>
      listWorkplaceAIProjects({
        queryParameters: args,
      }),
    options,
  )

export const useGetElasticsearchLinkCandidatesQuery = (
  projectId: ProjectID,
  options?: QueryHookOptions,
) =>
  useQuery<QueryFunctionReturnType<typeof getElasticsearchProjectLinkCandidates>>(
    makeQueryKey('adminGetElasticsearchProjectLinkCandidates', { id: projectId }),
    () =>
      getElasticsearchProjectLinkCandidates({
        pathParameters: { id: projectId },
      }),
    options,
  )

export const useGetObservabilityLinkCandidatesQuery = (
  projectId: ProjectID,
  options?: QueryHookOptions,
) =>
  useQuery<QueryFunctionReturnType<typeof getObservabilityProjectLinkCandidates>>(
    makeQueryKey('adminGetObservabilityProjectLinkCandidates', { id: projectId }),
    () =>
      getObservabilityProjectLinkCandidates({
        pathParameters: { id: projectId },
      }),
    options,
  )

export const useGetSecurityLinkCandidatesQuery = (
  projectId: ProjectID,
  options?: QueryHookOptions,
) =>
  useQuery<QueryFunctionReturnType<typeof getSecurityProjectLinkCandidates>>(
    makeQueryKey('adminGetSecurityProjectLinkCandidates', { id: projectId }),
    () =>
      getSecurityProjectLinkCandidates({
        pathParameters: { id: projectId },
      }),
    options,
  )
